import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useApi } from "@/lib/api";
import { fmtEur, fmtDate, productOf } from "@/lib/crm";

const MONTHS = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];

const Kpi = ({ label, value, testid, accent }) => (
  <div className="bg-white border border-slate-200/80 rounded-xl p-5">
    <p className="text-xs uppercase tracking-wider text-slate-500">{label}</p>
    <p data-testid={testid} className={`font-mono text-2xl font-semibold mt-2 ${accent || "text-slate-900"}`}>{value}</p>
  </div>
);

const Breakdown = ({ title, rows, labelOf, testid }) => {
  const max = Math.max(1, ...rows.map((r) => r.earned));
  return (
    <section className="bg-white border border-slate-200/80 rounded-xl p-6" data-testid={testid}>
      <h3 className="font-display font-semibold mb-4">{title}</h3>
      {!rows.length && <p className="text-sm text-slate-400">Nessuna pratica erogata nell'anno.</p>}
      <div className="space-y-3">
        {rows.map((r) => (
          <div key={labelOf(r)}>
            <div className="flex justify-between text-sm"><span>{labelOf(r)} <span className="text-slate-400">· {r.count}</span></span><span className="font-mono">{fmtEur(r.earned)}</span></div>
            <div className="h-1.5 bg-slate-100 rounded-full mt-1.5 overflow-hidden"><div className="h-full bg-sky-600 rounded-full" style={{ width: `${(r.earned / max) * 100}%` }} /></div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default function Commissions() {
  const [year, setYear] = useState(new Date().getFullYear());
  const { data } = useApi(`/reports/commissions?year=${year}`);
  const max = Math.max(1, ...(data?.months || []).map((m) => m.earned));

  return (
    <div className="space-y-6 fade-up" data-testid="commissions-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Provvigioni</h1>
          <p className="text-slate-500 mt-1">Calcolate sulle pratiche in fase "Erogato / Chiuso", per mese di chiusura.</p>
        </div>
        <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-full p-1">
          <button data-testid="year-prev" onClick={() => setYear(year - 1)} className="h-8 w-8 grid place-items-center rounded-full hover:bg-slate-100"><ChevronLeft className="h-4 w-4" /></button>
          <span data-testid="year-label" className="font-mono font-semibold px-2">{year}</span>
          <button data-testid="year-next" onClick={() => setYear(year + 1)} className="h-8 w-8 grid place-items-center rounded-full hover:bg-slate-100"><ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <Kpi label="Provvigioni incassate" value={fmtEur(data?.total || 0)} testid="kpi-commission-total" accent="text-emerald-600" />
        <Kpi label="Pratiche erogate" value={data?.count ?? 0} testid="kpi-commission-count" />
        <Kpi label="Previste in lavorazione" value={fmtEur(data?.expected || 0)} testid="kpi-commission-expected" accent="text-sky-700" />
      </div>

      <section className="bg-white border border-slate-200/80 rounded-xl p-6" data-testid="commission-months">
        <h3 className="font-display font-semibold mb-6">Andamento mensile</h3>
        <div className="flex items-end gap-2 sm:gap-3 h-48">
          {(data?.months || []).map((m) => (
            <div key={m.month} className="flex-1 flex flex-col items-center gap-2 h-full justify-end" title={`${fmtEur(m.earned)} · ${m.count} pratiche`}>
              {m.earned > 0 && <span className="text-[10px] font-mono text-slate-500 hidden sm:block">{Math.round(m.earned).toLocaleString("it-IT")}</span>}
              <div data-testid={`month-bar-${m.month}`} className="w-full rounded-t-md bg-sky-600 hover:bg-sky-500 transition-colors" style={{ height: `${(m.earned / max) * 100}%`, minHeight: m.earned ? 4 : 1, opacity: m.earned ? 1 : 0.15 }} />
              <span className="text-[11px] text-slate-500">{MONTHS[m.month - 1]}</span>
            </div>
          ))}
        </div>
      </section>

      <div className="grid lg:grid-cols-2 gap-6">
        <Breakdown title="Per prodotto" rows={data?.by_product || []} labelOf={(r) => productOf(r.product).label} testid="commission-by-product" />
        <Breakdown title="Per banca / istituto" rows={data?.by_bank || []} labelOf={(r) => r.bank} testid="commission-by-bank" />
      </div>

      <section className="bg-white border border-slate-200/80 rounded-xl p-6" data-testid="commission-practices">
        <h3 className="font-display font-semibold mb-4">Pratiche erogate</h3>
        {!data?.practices?.length && <p className="text-sm text-slate-400">Nessuna pratica erogata in questo anno. Inserisci la provvigione nella pratica e spostala in "Erogato / Chiuso".</p>}
        <div className="divide-y divide-slate-100">
          {(data?.practices || []).map((p) => (
            <Link key={p.practice_id} to={`/clienti/${p.client_id}`} className="flex items-center justify-between gap-4 py-3 hover:bg-slate-50 -mx-2 px-2 rounded">
              <div className="min-w-0">
                <p className="text-sm font-medium">{p.client_name}</p>
                <p className="text-xs text-slate-500">{productOf(p.product).label} · {p.bank || "—"} · {fmtDate(p.closed_on)}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="font-mono text-sm font-semibold text-emerald-600">{fmtEur(p.commission || 0)}</p>
                <p className="text-[11px] text-slate-400 font-mono">{fmtEur(p.amount)}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
